import * as React from 'react';
import type { PressEvent } from 'react-aria';

import type * as api from '@gitbook/api-client';
import type { BlockContentRefData } from '@gitbook/doc-core';
import { useHiveClient } from '@gitbook/hive-react';
import { MediaCard } from '@gitbook/react-uikit';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import {
    createInputContentRefPalette,
    useContentReference,
    useContentReferenceContext,
} from '../../ContentReferences';
import {
    BlockLoadingFallback,
    BlockWrapper,
    useBlockPaletteFactory,
    VoidBlockFrame,
} from '../common';

/**
 * Component to render a block content-ref.
 */
export function BlockContentRef(props: BlockProps) {
    const { editor, node } = props;
    const hiveClient = useHiveClient();
    const contentRefContext = useContentReferenceContext();
    const data = node.data.toFrozenObject() as BlockContentRefData;

    const createEditPalette = useEventCallback(() => {
        return createInputContentRefPalette(
            (ref) => {
                editor.change((change) => {
                    change.setDataByKey(node.key, ['ref'], ref);
                });
            },
            contentRefContext,
            hiveClient,
            {
                accept: ['page', 'space', 'collection'],
                search: { placeholder: 'Search in spaces and collections…' },
            }
        );
    });

    const palette = useBlockPaletteFactory({
        nodeKey: node.key,
        createExtraGroups: () => [
            {
                key: 'edit',
                commands: [
                    {
                        key: 'edit_ref',
                        title: 'Edit link target',
                        icon: Icon.PageLink,
                        trigger: () => createEditPalette(),
                    },
                ],
            },
        ],
    });

    return (
        <BlockWrapper {...props} palette={palette}>
            <VoidBlockFrame {...props}>
                <React.Suspense fallback={<BlockLoadingFallback />}>
                    <ContentRefCard contentRef={data.ref} readOnly={props.editor.readOnly} />
                </React.Suspense>
            </VoidBlockFrame>
        </BlockWrapper>
    );
}

/**
 * Inner card to display the content-ref, it can suspend rendering.
 */
function ContentRefCard(props: { contentRef: api.ContentRef; readOnly: boolean }) {
    const { contentRef, readOnly } = props;
    const resolved = useContentReference(contentRef);

    const linkProps = {
        href: resolved.href,
        onClick: (event: React.MouseEvent) => {
            if (!event.metaKey && !event.ctrlKey) {
                event.preventDefault();
            }
        },
        onPress: (event: PressEvent) => {
            if (!event.metaKey && !event.ctrlKey) {
                resolved.open();
            }
        },
    };

    return (
        <MediaCard
            title={resolved.title}
            icon={resolved.icon || resolved.secondaryIcon}
            buttons={
                readOnly ? null : (
                    <Button size="small" kind="secondary" label="View" {...linkProps} />
                )
            }
            {...(readOnly ? linkProps : {})}
        />
    );
}
